import type {
  SignerApplicantPutRequest,
  SignerPutAddressEntry,
  SignerPutAddressesSection,
  SignerPutApplicantSection,
  SignerPutBankSection,
  SignerPutEmailsSection,
  SignerPutEmploymentRow,
  SignerPutEmploymentsSection,
  SignerPutPhoneRow,
  SignerPutPhonesSection,
  SignerPutVlcExtrasSection,
} from "@credit-acceptance/step6-bff-client";

import { INCOME_TYPE_CODE_TO_ENUM } from "./constants";
import type { BankAccountInput, CustomerInfoInput, IncomeInput, PersonInput } from "./Signers.schema";

// ---------------------------------------------------------------------------
// Signers.putMapping
//
// Maps a form PersonInput (driver or 2nd signer) onto the BFF
// SignerApplicantPutRequest for the aggregate save endpoint (POST
// /api/v1/workbench/applications/{approvalId}). Pure functions — no React —
// so they stay unit-testable and reusable for both signer legs.
//
// Only fields that exist in the form AND are BFF-required today are mapped.
// Left unmapped for this ticket (no form input exists yet):
//   - bank.accountNumber
//   - applicant.applicantPk, applicant.driverLicenseState
//   - addresses.*.residenceOwnerType, monthlyRentMortgage, landlord; previous[]
//   - phones.rows[].isCallDnc
//   - emails.rows[].isTextOptIn
//   - employments.*.workLocation
//   - vlcExtras.cancelAutopayFlag
// ---------------------------------------------------------------------------

/**
 * Strips every non-digit character from a string.
 * @param {string} value - The raw string.
 * @returns {string} Digits only.
 */
function digitsOnly(value: string): string {
  return value.replaceAll(/\D/g, "");
}

/**
 * Converts a blank/whitespace-only string to undefined so optional BFF fields
 * are omitted rather than sent as empty strings.
 * @param {string} value - The raw string.
 * @returns {string | undefined} The trimmed value, or undefined when blank.
 */
function undefinedIfBlank(value: string): string | undefined {
  return value.trim() === "" ? undefined : value;
}

/**
 * Joins a street number and street name into one address line.
 * @param {string} streetNumber - The street number.
 * @param {string} streetName - The street name.
 * @returns {string | undefined} The joined line, or undefined when both are blank.
 */
function joinAddressLine(streetNumber: string, streetName: string): string | undefined {
  return undefinedIfBlank([streetNumber, streetName].filter(Boolean).join(" "));
}

/**
 * Maps the form bank account onto the BFF bank section.
 * @param {BankAccountInput} bankAccount - The form's bank account.
 * @returns {SignerPutBankSection} The BFF bank section.
 */
export function mapBankAccountToBankSection(bankAccount: BankAccountInput): SignerPutBankSection {
  return {
    bankName: undefinedIfBlank(bankAccount.bankName ?? ""),
    isCheckingAccount: bankAccount.isCheckingAccount,
    isSavingsAccount: bankAccount.isSavingsAccount,
    isPrepaidDebit: bankAccount.isPrepaidDebit,
  };
}

/**
 * Maps the form customer info's personal details onto the BFF applicant section.
 * @param {CustomerInfoInput} customerInfo - The form's customer information.
 * @returns {SignerPutApplicantSection} The BFF applicant section.
 */
export function mapCustomerInfoToApplicantSection(customerInfo: CustomerInfoInput): SignerPutApplicantSection {
  return {
    fullName: {
      first: undefinedIfBlank(customerInfo.firstName),
      middle: undefinedIfBlank(customerInfo.middleInitial),
      last: undefinedIfBlank(customerInfo.lastName),
      suffix: undefinedIfBlank(customerInfo.suffix),
    },
    dob: undefinedIfBlank(customerInfo.dateOfBirth),
    ssn: undefinedIfBlank(digitsOnly(customerInfo.ssnOrItin)),
    driverLicense: undefinedIfBlank(customerInfo.driverLicense),
  };
}

/**
 * Builds one BFF address entry from raw street/city/state/zip fields, or
 * undefined when every field is blank.
 * @param {string} streetNumber - Street number.
 * @param {string} streetName - Street name.
 * @param {string} apt - Apartment/suite number.
 * @param {string} zip - ZIP code.
 * @param {string} city - City.
 * @param {string} state - State.
 * @returns {SignerPutAddressEntry | undefined} The address entry, or undefined when empty.
 */
function mapAddressEntry(
  streetNumber: string,
  streetName: string,
  apt: string,
  zip: string,
  city: string,
  state: string
): SignerPutAddressEntry | undefined {
  const line1 = joinAddressLine(streetNumber, streetName);
  const line2 = undefinedIfBlank(apt);
  const mappedZip = undefinedIfBlank(zip);
  const mappedCity = undefinedIfBlank(city);
  const mappedState = undefinedIfBlank(state);

  if (!line1 && !line2 && !mappedZip && !mappedCity && !mappedState) {
    return undefined;
  }

  return { line1, line2, city: mappedCity, state: mappedState, zip: mappedZip };
}

/**
 * Maps the form customer info's physical/mailing addresses onto the BFF
 * addresses section. A mailing address is only sent when it differs from the
 * physical one (`mailingSameAsCurrent` is false); `undefined` tells the BFF to
 * leave the mailing address as-is.
 * @param {CustomerInfoInput} customerInfo - The form's customer information.
 * @returns {SignerPutAddressesSection} The BFF addresses section.
 */
export function mapCustomerInfoToAddressesSection(customerInfo: CustomerInfoInput): SignerPutAddressesSection {
  const current = mapAddressEntry(
    customerInfo.physicalStreetNumber,
    customerInfo.physicalStreetName,
    customerInfo.physicalApt,
    customerInfo.physicalZip,
    customerInfo.physicalCity,
    customerInfo.physicalState
  );

  const mailing = customerInfo.mailingSameAsCurrent
    ? undefined
    : mapAddressEntry(
        customerInfo.mailingStreetNumber,
        customerInfo.mailingStreetName,
        customerInfo.mailingApt,
        customerInfo.mailingZip,
        customerInfo.mailingCity,
        customerInfo.mailingState
      );

  return { current, mailing };
}

/**
 * Maps the form customer info's phone rows onto the BFF phones section. Rows
 * with no digits entered are dropped.
 * @param {CustomerInfoInput} customerInfo - The form's customer information.
 * @returns {SignerPutPhonesSection} The BFF phones section.
 */
export function mapCustomerInfoToPhonesSection(customerInfo: CustomerInfoInput): SignerPutPhonesSection {
  const rows: SignerPutPhoneRow[] = customerInfo.phones
    .map((phone) => ({ type: phone.type.toUpperCase(), number: digitsOnly(phone.number) }))
    .filter((phone) => phone.number.length > 0);

  return { rows };
}

/**
 * Maps the form customer info's email onto the BFF emails section.
 * @param {CustomerInfoInput} customerInfo - The form's customer information.
 * @returns {SignerPutEmailsSection} The BFF emails section.
 */
export function mapCustomerInfoToEmailsSection(customerInfo: CustomerInfoInput): SignerPutEmailsSection {
  const email = undefinedIfBlank(customerInfo.email);

  return {
    rows: email ? [{ emailAddress: email }] : [],
    isEmailAvailable: !customerInfo.emailNotAvailable,
  };
}

/**
 * Maps the form customer info's Hold Coupon Book selection onto the BFF vlcExtras section.
 * @param {CustomerInfoInput} customerInfo - The form's customer information.
 * @returns {SignerPutVlcExtrasSection} The BFF vlcExtras section.
 */
export function mapCustomerInfoToVlcExtrasSection(customerInfo: CustomerInfoInput): SignerPutVlcExtrasSection {
  return { holdCouponBook: customerInfo.holdCouponBook };
}

/**
 * Maps a single form income block onto a BFF employment row. `incomeType`
 * carries the BFF's income-type enum value; `incomeTypeCode` (despite its
 * name) carries the pay-frequency enum value.
 * @param {IncomeInput} income - A form income block.
 * @returns {SignerPutEmploymentRow} The BFF employment row.
 */
function mapIncomeToEmploymentRow(income: IncomeInput): SignerPutEmploymentRow {
  return {
    incomeType: INCOME_TYPE_CODE_TO_ENUM[income.incomeType],
    incomeTypeCode: income.payFrequency,
    payType: income.grossNet,
    grossPay: income.grossMonthlyPay,
    employerPhone: undefinedIfBlank(digitsOnly(income.verificationPhone)),
    employerName: undefinedIfBlank(income.employerName),
  };
}

/**
 * Maps the form's income blocks onto the BFF employments section. The first
 * three blocks map to primary/secondary/tertiary; `totalGrossMonthlyIncome` is
 * the sum of every block's gross monthly pay.
 * @param {IncomeInput[]} incomes - The form's income blocks (1-3).
 * @returns {SignerPutEmploymentsSection} The BFF employments section.
 */
export function mapIncomesToEmploymentsSection(incomes: IncomeInput[]): SignerPutEmploymentsSection {
  const [primary, secondary, tertiary] = incomes;

  return {
    primary: primary ? mapIncomeToEmploymentRow(primary) : undefined,
    secondary: secondary ? mapIncomeToEmploymentRow(secondary) : undefined,
    tertiary: tertiary ? mapIncomeToEmploymentRow(tertiary) : undefined,
    totalGrossMonthlyIncome: incomes.reduce((total, income) => total + income.grossMonthlyPay, 0),
  };
}

/**
 * Builds the full BFF `SignerApplicantPutRequest` from a form person (driver or
 * 2nd signer), for the `signerApplicant` / `secondSignerApplicant` legs of the
 * aggregate save payload. `vlcExtras` is only sent for the 2nd-signer leg.
 * @param {PersonInput} person - The form's driver or 2nd-signer state.
 * @param {boolean} isSecondSigner - Whether this is the 2nd-signer leg.
 * @returns {SignerApplicantPutRequest} The BFF signer-applicant PUT request.
 */
export function buildSignerApplicantPutRequest(
  person: PersonInput,
  isSecondSigner: boolean
): SignerApplicantPutRequest {
  return {
    applicant: mapCustomerInfoToApplicantSection(person.customerInfo),
    addresses: mapCustomerInfoToAddressesSection(person.customerInfo),
    phones: mapCustomerInfoToPhonesSection(person.customerInfo),
    emails: mapCustomerInfoToEmailsSection(person.customerInfo),
    employments: mapIncomesToEmploymentsSection(person.incomes),
    bank: mapBankAccountToBankSection(person.bankAccount),
    vlcExtras: isSecondSigner ? mapCustomerInfoToVlcExtrasSection(person.customerInfo) : undefined,
  };
}
